import { useContext, useState, useEffect, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import useWorkspaceRoles from '@/hooks/useWorkspaceRoles';
import { LangContext } from '@/context/LangProvider';
import { Textarea } from '@/components/ui/textarea';
import RequiredLabel from '@/components/labels/RequiredLabel';
import useCreateProjectStore from '@/store/CreateProjectStore';
import { DiscussionGuideProps, InterviewQuestion, InterviewQuestions } from '@/types';
import DeleteQuestionModal from '@/components/Modals/DeleteQuestionModal';
import { useDiscussionGuideHandlers, useDragAndDropHandlers } from '@/hooks';
import {
  isGroup,
  shouldRenderGroupUI,
  wrapQuestionsInGroup,
  flattenGroupToQuestions,
} from '@/utils';
import { QuestionGroupLimits } from '@/enums';
import { DiscussionGuideHeader } from './discussion-guide/DiscussionGuideHeader';
import { GroupModeView } from './discussion-guide/GroupModeView';
import { FlatModeView } from './discussion-guide/FlatModeView';

// Helper function to create a fresh initial question (deep copy)
const createInitialQuestion = (): Omit<InterviewQuestion, 'id'> => ({
  question: '',
  follow_up_question: '0',
  media: undefined,
  answer_quantification: [
    {
      quantify_answer: false,
      value: '',
    },
  ],
});

export function DiscussionGuide({
  singleProjectData,
  isProjectPublished,
  isProjectCompleted,
  register,
  control,
  setValue,
  clearErrors,
  setFileData,
  fileData,
  currentQuestions,
  errors,
}: DiscussionGuideProps) {
  const { lang } = useContext(LangContext);
  const {
    setIsFormChanged,
    setReorderChanged,
    setInterviewQuestionsStructureChanged,
    uploadingQuestionId,
  } = useCreateProjectStore();
  const { isUserViewer } = useWorkspaceRoles();

  const [groupStructure, setGroupStructure] = useState<InterviewQuestions>(() => {
    if (currentQuestions) {
      return currentQuestions;
    }
    return wrapQuestionsInGroup([], 'Interview Questions', false);
  });

  const [isActiveDrag, setIsActiveDrag] = useState(false);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [questionToDelete, setQuestionToDelete] = useState<string | null>(null);
  const [isDeletingQuestion, setIsDeletingQuestion] = useState(false);
  const [openQuestionId, setOpenQuestionId] = useState<string | null>(null);

  // Track if we're updating internally to prevent sync loop
  const isInternalUpdate = useRef(false);

  const showGroupUI = shouldRenderGroupUI(groupStructure);
  const flatQuestions = flattenGroupToQuestions(groupStructure);

  // Check if question exists in saved database
  const isQuestionInDatabase = (questionId: string): boolean => {
    if (!questionId || !singleProjectData?.interview_questions?.children) {
      return false;
    }

    const searchInChildren = (children: any[]): boolean => {
      for (const child of children) {
        if (child.id === questionId) {
          return true;
        }
        if (child.children && Array.isArray(child.children)) {
          if (searchInChildren(child.children)) {
            return true;
          }
        }
      }
      return false;
    };

    return searchInChildren(singleProjectData.interview_questions.children);
  };

  // Sync local state with form value when it changes from external source (e.g., reset after save)
  useEffect(() => {
    if (currentQuestions && !isInternalUpdate.current) {
      // Only sync if currentQuestions has valid structure with children array
      if (Array.isArray(currentQuestions.children)) {
        setGroupStructure(currentQuestions);
      } else if (currentQuestions.children === undefined) {
        // If children is undefined, don't sync (keep current structure)
      } else {
        // If children exists but is not an array, fix the structure
        setGroupStructure({
          ...currentQuestions,
          children: [],
        });
      }
    }
    // Reset the flag after sync
    if (isInternalUpdate.current) {
      isInternalUpdate.current = false;
    }
  }, [currentQuestions]);

  // Update form value whenever group structure changes
  const updateFormValue = (newStructure: InterviewQuestions) => {
    isInternalUpdate.current = true; // Mark as internal update
    setGroupStructure(newStructure);
    setValue('interview_questions', newStructure, { shouldDirty: true });
    setIsFormChanged(true);
  };

  // Helper function to update group at path
  const updateGroupAtPath = (
    structure: InterviewQuestions,
    path: number[],
    updates: Partial<InterviewQuestions>,
    depth: number = 0,
  ): InterviewQuestions => {
    if (path.length === 0 || depth > path.length - 1) {
      return { ...structure, ...updates };
    }

    const targetIndex = path[depth];
    const childrenAsGroups = structure.children as InterviewQuestions[];
    return {
      ...structure,
      children: childrenAsGroups.map((child, idx) => {
        if (idx === targetIndex && isGroup(child)) {
          return updateGroupAtPath(child, path, updates, depth + 1);
        }
        return child;
      }),
    };
  };

  const {
    handleDragStart: dragStartHandler,
    handleDragEnd: dragEndHandler,
    handleReorderQuestionsInGroup,
    handleReorderSubgroupsInGroup,
    handleMoveQuestionBetweenGroups,
  } = useDragAndDropHandlers({
    groupStructure,
    showGroupUI,
    setGroupStructure: updateFormValue,
    setIsFormChanged,
    setReorderChanged,
  });

  const {
    handleCreateGroup,
    handleGroupNameChange,
    handleGroupRandomizeChange,
    handleGlobalRandomizeToggle,
    handleGroupDelete,
    handleAddQuestionWithOpen,
    handleAddSubgroupToGroup,
    handleDeleteQuestion: deleteQuestionHandler,
    confirmDeleteQuestion: confirmDeleteQuestionHandler,
  } = useDiscussionGuideHandlers({
    groupStructure,
    singleProjectData,
    setGroupStructure: updateFormValue,
    updateGroup: (path, updates) => {
      if (path.length === 0) {
        updateFormValue({ ...groupStructure, ...updates });
      } else {
        updateFormValue(updateGroupAtPath(groupStructure, path, updates));
      }
    },
    setIsFormChanged,
    setInterviewQuestionsStructureChanged,
    setValue,
    setOpenQuestionId,
  });

  const handleAddSubgroupWithOpen = (groupIndex: number, groupPath?: number[]) => {
    const questionId = handleAddSubgroupToGroup(groupIndex, groupPath);
    if (questionId) setOpenQuestionId(questionId);
  };

  const confirmDeleteQuestion = async () => {
    await confirmDeleteQuestionHandler(
      questionToDelete,
      setIsDeletingQuestion,
      setIsDeleteModalOpen,
      setQuestionToDelete,
    );
  };

  const addQuestion = () => {
    const newQuestion: InterviewQuestion = { ...createInitialQuestion(), id: uuidv4() };

    // Ensure groupStructure has children array
    if (!groupStructure || !Array.isArray(groupStructure.children)) {
      return;
    }

    // If in group mode and groups exist, create a new group with the question
    if (showGroupUI && groupStructure.children.length > 0) {
      const existingGroups = groupStructure.children.filter((child) =>
        isGroup(child),
      ) as InterviewQuestions[];
      const groupNumber = existingGroups.length + 1;

      const newGroup: InterviewQuestions = {
        id: uuidv4(),
        name: `Group ${groupNumber}`,
        randomize: false,
        children: [newQuestion],
      };

      updateFormValue({
        ...groupStructure,
        children: [...existingGroups, newGroup] as InterviewQuestions[],
      });
    } else {
      // Add question to root
      const existingQuestions = groupStructure.children as InterviewQuestion[];
      updateFormValue({
        ...groupStructure,
        children: [...existingQuestions, newQuestion] as InterviewQuestion[],
      });
    }

    setOpenQuestionId(newQuestion.id);
  };

  // Check if root-level randomization is enabled
  const isRootRandomized = groupStructure.randomize === true;

  const isDisabled = isUserViewer || isProjectPublished || isProjectCompleted;

  const existingGroupsCount = groupStructure?.children?.filter(isGroup).length || 0;

  // Top-level "Add Question" button: disabled when question limit OR group limit reached
  const isTopLevelAddQuestionDisabled =
    isDisabled ||
    flatQuestions.length >= QuestionGroupLimits.MAX_QUESTIONS ||
    existingGroupsCount >= QuestionGroupLimits.MAX_GROUPS ||
    !!uploadingQuestionId;

  // Group-level "Add Question" buttons (+ icons): disabled only when question limit reached
  const isGroupLevelAddQuestionDisabled =
    isDisabled ||
    flatQuestions.length >= QuestionGroupLimits.MAX_QUESTIONS ||
    !!uploadingQuestionId;

  const hasQuestions = flatQuestions.length > 0;

  const isCreatingGroupDisabled =
    isDisabled ||
    existingGroupsCount >= QuestionGroupLimits.MAX_GROUPS ||
    flatQuestions.length >= QuestionGroupLimits.MAX_QUESTIONS;

  // Group related props
  const formProps = { register, control, setValue, clearErrors, errors };
  const projectProps = { isProjectPublished, isProjectCompleted, singleProjectData };
  const questionProps = {
    questions: flatQuestions,
    openQuestionId,
    setOpenQuestionId,
    fileData,
    setFileData,
    currentQuestions: currentQuestions ? flattenGroupToQuestions(currentQuestions) : [],
  };
  const groupHandlers = {
    onGroupNameChange: handleGroupNameChange,
    onGroupRandomizeChange: handleGroupRandomizeChange,
    onGroupDelete: handleGroupDelete,
    onAddQuestion: handleAddQuestionWithOpen,
    onAddSubgroup: handleAddSubgroupWithOpen,
    onReorderQuestions: handleReorderQuestionsInGroup,
    onReorderSubgroups: handleReorderSubgroupsInGroup,
    onMoveQuestionBetweenGroups: handleMoveQuestionBetweenGroups,
  };

  return (
    <>
      <div className="w-full h-full pl-1">
        <div className="flex flex-col w-full gap-6">
          {/* -- introduction -- */}
          <RequiredLabel title="openingMessage" tooltip={lang.get('msg.openingMessageInfo')}>
            <Textarea
              disabled={isUserViewer || isProjectPublished || isProjectCompleted}
              {...register('opening_message')}
              placeholder={lang.get('msg.openingMessagePlaceholder')}
              onChange={(e) => {
                setIsFormChanged(true);
                setValue('opening_message', e.target.value, { shouldDirty: true });
              }}
              className="h-24"
            />
          </RequiredLabel>

          {/* -- Interview Questions Section -- */}
          <div className="flex flex-col w-full gap-4">
            <DiscussionGuideHeader
              showGroupUI={showGroupUI}
              hasQuestions={hasQuestions}
              isRootRandomized={isRootRandomized}
              isAddingQuestionsDisabled={isTopLevelAddQuestionDisabled}
              isDisabled={isDisabled}
              isCreatingGroupDisabled={isCreatingGroupDisabled}
              currentQuestionCount={flatQuestions.length}
              onAddQuestion={addQuestion}
              onCreateGroup={handleCreateGroup}
              onGlobalRandomizeToggle={handleGlobalRandomizeToggle}
            />

            {/* Scrollable content area */}
            <div className="flex flex-col w-full gap-4 max-h-[calc(100vh-400px)] overflow-y-auto pr-4 pl-1">
              {/* Conditional rendering: Group UI or Flat List */}
              {showGroupUI ? (
                <GroupModeView
                  structure={{
                    groupStructure,
                    isDisabled,
                    isAddingQuestionsDisabled: isGroupLevelAddQuestionDisabled,
                  }}
                  dragAndDrop={{
                    isActiveDrag,
                    activeId,
                    onDragEnd: (event) => dragEndHandler(event, setIsActiveDrag, setActiveId),
                    onDragStart: (event) => dragStartHandler(event, setIsActiveDrag, setActiveId),
                  }}
                  handlers={{
                    ...groupHandlers,
                    onDeleteQuestion: (idToDelete) =>
                      deleteQuestionHandler(idToDelete, setQuestionToDelete, setIsDeleteModalOpen),
                    isQuestionInDatabase,
                  }}
                  formProps={formProps}
                  projectProps={projectProps}
                  questionProps={questionProps}
                />
              ) : (
                <FlatModeView
                  structure={{
                    isRootRandomized,
                    hasQuestions,
                    isDisabled,
                    isUserViewer,
                  }}
                  dragAndDrop={{
                    onDragEnd: (event) => dragEndHandler(event, setIsActiveDrag, setActiveId),
                    onDragStart: (event) => dragStartHandler(event, setIsActiveDrag, setActiveId),
                  }}
                  handlers={{
                    onDeleteQuestion: (idToDelete) =>
                      deleteQuestionHandler(idToDelete, setQuestionToDelete, setIsDeleteModalOpen),
                    isQuestionInDatabase,
                  }}
                  formProps={formProps}
                  projectProps={projectProps}
                  questionProps={questionProps}
                />
              )}
            </div>
          </div>
        </div>
      </div>
      <DeleteQuestionModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setQuestionToDelete(null);
        }}
        onConfirm={confirmDeleteQuestion}
        hasMedia={
          questionToDelete
            ? !!flatQuestions.find(({ id }) => id === questionToDelete)?.media
            : false
        }
        isDeletingQuestion={isDeletingQuestion}
      />
    </>
  );
}
